import { isBillingInterval, isPaidPlan, type BillingInterval, type PaidPlan } from './plans'

/** Where a new free account lands: the new-monitor form. No checkout. */
export const FREE_START_PATH = '/dashboard/monitors/new'

const STORAGE_KEY = 'uptrack_redirect'

// The emailed link may carry 200 bytes at most: the server (`auth_api::safe_redirect`) drops more.
const LINK_MAX = 200
// A path kept in this browser may be longer. An OAuth consent URL (client, redirect URI, PKCE
// challenge, state, resource) is about 400 bytes.
const NEXT_MAX = 2048

function isSameOriginPath(r: unknown, max: number): r is string {
  return (
    typeof r === 'string' &&
    r.startsWith('/') &&
    !r.startsWith('//') &&
    !r.includes('\\') &&
    r.length <= max &&
    // eslint-disable-next-line no-control-regex
    !/[\u0000-\u001f\u007f]/.test(r)
  )
}

// Same rule as the server (`auth_api::safe_redirect`): a same-origin relative
// path only, so a redirect can never send the user to another site.
export function isSafeRedirect(r: unknown): r is string {
  return isSameOriginPath(r, LINK_MAX)
}

/** Same rule as `isSafeRedirect` with a longer limit, for a return path that stays in this browser. */
export function isSafeNext(r: unknown): r is string {
  return isSameOriginPath(r, NEXT_MAX)
}

/** What the emailed link may carry: a short safe path. A longer path stays in this browser (`rememberRedirect`). */
export function linkRedirect(target: string | undefined): string | undefined {
  return isSafeRedirect(target) ? target : undefined
}

/**
 * Where to go after a magic link signs the user in. The redirect in the link wins (it works
 * across devices), then the path this browser stored (for example a long OAuth consent URL),
 * then the dashboard. Both values are checked again here, so a tampered value never leaves the site.
 */
export function afterSignInPath(fromLink: unknown): string {
  const stored = takeRememberedRedirect()
  return isSafeRedirect(fromLink) ? fromLink : (stored ?? '/dashboard')
}

export function checkoutPath(plan: PaidPlan, interval: BillingInterval): string {
  return `/checkout?plan=${plan}&interval=${interval}`
}

// Where to go after sign-in: a chosen plan wins (a paid plan goes straight to
// checkout, `free` to the new-monitor form), then an explicit safe redirect.
// Interval defaults to annual, as on /pricing.
export function authRedirectTarget(search: { plan?: unknown; interval?: unknown; redirect?: unknown }): string | undefined {
  if (isPaidPlan(search.plan)) {
    return checkoutPath(search.plan, isBillingInterval(search.interval) ? search.interval : 'annual')
  }
  if (search.plan === 'free') return FREE_START_PATH
  return isSafeNext(search.redirect) ? search.redirect : undefined
}

// localStorage survives the OAuth round-trip in the same browser. It can throw
// (private mode, blocked storage), so every access is guarded.
// `undefined` clears any stale value, so an abandoned /signup?plan=... visit
// cannot send a later plain login to checkout.
export function rememberRedirect(target: string | undefined) {
  try {
    if (isSafeNext(target)) localStorage.setItem(STORAGE_KEY, target)
    else localStorage.removeItem(STORAGE_KEY)
  } catch {
    // Storage unavailable: the magic link still carries the redirect.
  }
}

export function takeRememberedRedirect(): string | undefined {
  try {
    const saved = localStorage.getItem(STORAGE_KEY)
    localStorage.removeItem(STORAGE_KEY)
    return isSafeNext(saved) ? saved : undefined
  } catch {
    return undefined
  }
}
